import React, { useState } from 'react';
import { Column as ColumnType, Card as CardType, cardApi, getApiErrorMessage, getCardApiErrorMessage, wasBoardAccessDenied } from '../services/api';
import { Card } from './Card';
import { ConfirmModal } from './ConfirmModal';
import { Check, Xmark, Plus, Trash } from 'iconoir-react';
import { PremiumLimitModal } from './PremiumLimitModal';
import { CardQuotaWarningToast } from './CardQuotaWarningToast';

interface ColumnProps {
  column: ColumnType;
  boardId: number;
  onUpdate: (column: ColumnType) => void;
  onDelete: (columnId: number) => void;
  onCardMove: (card: CardType, fromColumnId: number, toColumnId: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, columnId: number) => void;
  onDragStart: (card: CardType) => void;
  onDragEnd: () => void;
  isDragOver: boolean;
  onRefresh: () => void;
  onColumnDragStart?: (column: ColumnType) => void;
  onColumnDragEnd?: () => void;
  onColumnDrop?: (e: React.DragEvent, targetColumn: ColumnType) => void;
  isColumnDragOver?: boolean;
  userRole?: string;
  cardIdToOpen?: number | null;
}

export const Column: React.FC<ColumnProps> = ({
  column,
  boardId,
  onUpdate,
  onDelete,
  onCardMove,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
  isDragOver,
  onRefresh,
  onColumnDragStart,
  onColumnDragEnd,
  onColumnDrop,
  isColumnDragOver = false,
  userRole = 'visitor',
  cardIdToOpen = null
}) => {
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [newCardTitle, setNewCardTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(column.title);
  const [dragOverCardId, setDragOverCardId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPremiumLimitModal, setShowPremiumLimitModal] = useState(false);
  const [cardLimitInfo, setCardLimitInfo] = useState<{
    reason: 'plan_limit_exceeded' | 'overage_payment_required';
    limit: number | null;
  } | null>(null);

  const [showQuotaWarningToast, setShowQuotaWarningToast] = useState(false);

  const isAdmin = userRole === 'admin';
  const canEdit = userRole === 'admin' || userRole === 'editor';
  const canReorderColumns = userRole === 'admin';

  const handleAddCard = async () => {
    if (newCardTitle.trim()) {
      try {
        const response = await cardApi.create(column.id, {
          title: newCardTitle.trim(),
          position: column.cards.length
        });
        setNewCardTitle('');
        setIsAddingCard(false);
        if (response.data.quota_warning) {
          setShowQuotaWarningToast(true);
        }
        onRefresh();
      } catch (err: any) {
        if (
          err.response?.status === 402 &&
          err.response?.data?.error === 'quota_exceeded'
        ) {
          setCardLimitInfo({
            reason: err.response.data.reason === 'overage_payment_required'
              ? 'overage_payment_required'
              : 'plan_limit_exceeded',
            limit: err.response.data.limit ?? null,
          });
          setShowPremiumLimitModal(true);
        } else if (err.response?.status === 403) {
          await onRefresh();
          if (!wasBoardAccessDenied()) {
            window.alert(getApiErrorMessage(err, 'Vous n\'avez pas la permission de créer des cartes.'));
          }
        } else {
          console.error('Erreur lors de la création de la carte', err);
        }
      }
    }
  };

  const handleDeleteCard = async (cardId: number) => {
    try {
      await cardApi.delete(column.id, cardId);
      onRefresh();
    } catch (err: any) {
      console.error('Erreur lors de la suppression de la carte', err);
      if (err.response?.status === 403) {
        await onRefresh();
        if (!wasBoardAccessDenied()) {
          window.alert(getApiErrorMessage(err, 'Vous n\'avez pas la permission de supprimer des cartes.'));
        }
      }
    }
  };

  const handleUpdateCard = async (updatedCard: CardType) => {
    try {
      await cardApi.update(column.id, updatedCard.id, {
        title: updatedCard.title,
        description: updatedCard.description,
        label_color: updatedCard.label_color,
        start_date: updatedCard.start_date,
        due_date: updatedCard.due_date
      });
      onRefresh();
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour de la carte', err);
      if (err.response?.status === 403) {
        await onRefresh();
        // Carte verrouillée entre-temps (rare) : jamais le code technique brut.
        if (!wasBoardAccessDenied()) {
          window.alert(getCardApiErrorMessage(err, 'Vous n\'avez pas la permission de modifier des cartes.'));
        }
      }
    }
  };

  const handleArchiveCard = async (cardId: number, archived: boolean) => {
    try {
      await cardApi.update(column.id, cardId, {
        archived: archived
      });
      onRefresh();
    } catch (err: any) {
      console.error('Erreur lors de l\'archivage de la carte', err);
      if (err.response?.status === 403) {
        await onRefresh();
        if (!wasBoardAccessDenied()) {
          window.alert(getCardApiErrorMessage(err, 'Vous n\'avez pas la permission de modifier des cartes.'));
        }
      }
    }
  };

  const handleSaveTitle = () => {
    if (editTitle.trim()) {
      onUpdate({ ...column, title: editTitle.trim() });
      setIsEditingTitle(false);
    }
  };

  const handleCancelTitle = () => {
    setEditTitle(column.title);
    setIsEditingTitle(false);
  };

  const handleCardDragOver = (e: React.DragEvent, cardId: number) => {
    if (!canEdit) return;
    
    e.preventDefault();
    e.stopPropagation();
    setDragOverCardId(cardId);
  };

  const handleCardDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCardId(null);
  };

  const handleCardDrop = async (e: React.DragEvent, targetCard: CardType) => {
    if (!canEdit) return;
    
    e.preventDefault();
    e.stopPropagation();
    setDragOverCardId(null);
    
    // On récupère la carte en cours de déplacement depuis le dataTransfer
    const draggedCardData = e.dataTransfer.getData('card');
    if (!draggedCardData) return;
    
    const draggedCard: CardType = JSON.parse(draggedCardData);
    
    // Si c'est la même carte, on ne fait rien
    if (draggedCard.id === targetCard.id) return;
    
    // Calculer la nouvelle position
    const targetPosition = targetCard.position;
    
    try {
      // Si c'est dans la même colonne, on réordonne
      if (draggedCard.column_id === column.id) {
        await cardApi.update(column.id, draggedCard.id, {
          position: targetPosition
        });
      } else {
        // Si c'est une autre colonne, on déplace
        await cardApi.move(draggedCard.id, {
          column_id: column.id,
          position: targetPosition
        });
      }
      onRefresh();
    } catch (err: any) {
      console.error('Erreur lors du déplacement de la carte', err);
      if (err.response?.status === 403) {
        await onRefresh();
        if (!wasBoardAccessDenied()) {
          window.alert(getCardApiErrorMessage(err, 'Vous n\'avez pas la permission de déplacer des cartes.'));
        }
      }
    }
  };

  const handleColumnDragStart = (e: React.DragEvent) => {
    if (canReorderColumns && onColumnDragStart) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('column', JSON.stringify(column));
      onColumnDragStart(column);
    }
  };

  const handleColumnDragEnd = () => {
    if (onColumnDragEnd) {
      onColumnDragEnd();
    }
  };

  const handleColumnDragOver = (e: React.DragEvent) => {
    if (canReorderColumns) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleColumnDrop = (e: React.DragEvent) => {
    if (canReorderColumns && onColumnDrop) {
      e.preventDefault();
      e.stopPropagation();
      onColumnDrop(e, column);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDelete(column.id);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <div
        className={`kanban-column ${isDragOver ? 'drag-over' : ''} ${isColumnDragOver ? 'column-drag-over' : ''}`}
        onDragOver={(e) => {
          onDragOver(e);
          handleColumnDragOver(e);
        }}
        onDragLeave={onDragLeave}
        onDrop={(e) => {
          onDrop(e, column.id);
          handleColumnDrop(e);
        }}
      >
        <div className="kanban-column-header">
          {canReorderColumns && (
            <div 
              className="column-drag-handle" 
              title="Glisser pour déplacer la colonne"
              draggable={true}
              onDragStart={handleColumnDragStart}
              onDragEnd={handleColumnDragEnd}
            >
              ⋮⋮
            </div>
          )}
          {isEditingTitle ? (
            <div
              style={{
                display: 'flex',
                gap: '6px',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden'
              }}
            >
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  color: 'white',
                  fontSize: '14px',
                  flex: 1,
                  minWidth: 0,
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
              <button onClick={handleSaveTitle} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', flexShrink: 0 }}>
                <Check width={18} height={18} strokeWidth={2} />
              </button>
              <button onClick={handleCancelTitle} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', flexShrink: 0 }}>
                <Xmark width={18} height={18} strokeWidth={2} />
              </button>
            </div>
          ) : (
            <div
              onClick={() => {
                if (canEdit) {
                  setIsEditingTitle(true);
                }
              }}
              style={{
                cursor: canEdit ? 'pointer' : 'default',
                flex: 1,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis'
              }}
              title={column.title}
            >
              {column.title}
            </div>
          )}
          {isAdmin && (
            <button
              className="delete-btn"
              onClick={handleDeleteClick}
              style={{ color: 'white' }}
              title="Supprimer la colonne"
            >
              <Trash width={18} height={18} strokeWidth={2} />
            </button>
          )}
        </div>

      <div className="kanban-column-content">
        {column.cards.filter(card => !card.archived).map(card => (
          <div
            key={card.id}
            onDragOver={(e) => handleCardDragOver(e, card.id)}
            onDragLeave={handleCardDragLeave}
            onDrop={(e) => handleCardDrop(e, card)}
            className={dragOverCardId === card.id ? 'card-drop-zone' : ''}
          >
            <Card
              card={card}
              onUpdate={handleUpdateCard}
              onDelete={handleDeleteCard}
              onArchive={handleArchiveCard}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              initiallyOpen={cardIdToOpen === card.id}
              userRole={userRole}
              boardId={boardId}
              onRefresh={onRefresh}
            />
          </div>
        ))}

        {canEdit && (
          isAddingCard ? (
          <div className="card-form">
            <textarea
              value={newCardTitle}
              onChange={(e) => setNewCardTitle(e.target.value)}
              placeholder="Titre de la carte..."
              maxLength={144}
              autoFocus
            />
            <div className="card-form-buttons">
              <button className="btn btn-primary" onClick={handleAddCard}>
                Ajouter
              </button>
              <button className="btn btn-cancel" onClick={() => {
                setIsAddingCard(false);
                setNewCardTitle('');
              }}>
                Annuler
              </button>
            </div>
          </div>
        ) : (
            <button
              className="add-card-btn"
              onClick={() => setIsAddingCard(true)}
            >
              <Plus width={16} height={16} strokeWidth={2} />
              <span>Ajouter une carte</span>
            </button>
          )
        )}
      </div>
    </div>

    <ConfirmModal
      isOpen={showDeleteConfirm}
      title="Supprimer la colonne"
      message={`Êtes-vous sûr de vouloir supprimer la colonne "${column.title}" ? Toutes les cartes qu'elle contient seront également supprimées.`}
      confirmText="Supprimer"
      cancelText="Annuler"
      onConfirm={handleConfirmDelete}
      onCancel={() => setShowDeleteConfirm(false)}
      isDangerous={true}
    />
    
    <PremiumLimitModal
      isOpen={showPremiumLimitModal}
      onClose={() => {
        setShowPremiumLimitModal(false);
        setCardLimitInfo(null);
      }}
      message={
        cardLimitInfo?.reason === 'overage_payment_required' ? (
          <>
            Vous avez atteint votre limite de <strong>{cardLimitInfo.limit ?? ''} cartes</strong> incluse dans votre offre Premium.
          </>
        ) : (
          <>
            Vous avez atteint la limite de <strong>{cardLimitInfo?.limit ?? 100} cartes</strong> incluse dans votre offre gratuite.
          </>
        )
      }
      premiumMessage={
        cardLimitInfo?.reason === 'overage_payment_required' ? (
          <>
            Achetez un <strong>dépassement de cartes</strong> depuis Mon compte &gt; Dépassements pour continuer à en créer.
          </>
        ) : (
          <>
            Découvrez nos <strong>offres Premium</strong> pour créer davantage de cartes,
            supprimer les publicités et profiter de fonctionnalités supplémentaires.
          </>
        )
      }
      ctaLabel={cardLimitInfo?.reason === 'overage_payment_required' ? 'Gérer mes dépassements' : undefined}
      ctaTarget={cardLimitInfo?.reason === 'overage_payment_required' ? 'overages' : undefined}
      closeLabel={cardLimitInfo?.reason === 'overage_payment_required' ? 'Fermer' : undefined}
    />

    {showQuotaWarningToast && (
      <CardQuotaWarningToast onClose={() => setShowQuotaWarningToast(false)} />
    )}
    </>
  );
};
